import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from "../Assets/images/mlogo.png";
import { useWeather, getWeatherInfo } from "../context/WeatherContext";
import { getNewsByFilter } from "../api/apiHandler";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // Track open mobile dropdowns
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [currentDate, setCurrentDate] = useState("");
  const [currentDateShort, setCurrentDateShort] = useState("");
  const [currentTime, setCurrentTime] = useState("");

  const [isListening, setIsListening] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [breakingNews, setBreakingNews] = useState([]);
  const { weather } = useWeather();

  const weatherInfo = weather ? getWeatherInfo(weather.code) : null;
  const weatherIcon = weatherInfo
    ? weatherInfo.icon === "fa-sun" && !weather.isDay
      ? "fa-moon"
      : weatherInfo.icon
    : "fa-cloud-sun";
  const weatherLabel = weatherInfo
    ? i18n.language === "en"
      ? weatherInfo.en
      : weatherInfo.hi
    : "";
  // Voice Search Logic
  const handleVoiceSearch = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert("आपका ब्राउज़र वॉइस सर्च का समर्थन नहीं करता है।");
      return;
    }

    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      return;
    }

    const recognition = new SR();
    recognitionRef.current = recognition;
    recognition.lang = i18n.language === "en" ? "en-IN" : "hi-IN";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setIsListening(true);

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setSearchQuery(transcript);
      navigate(`/search?q=${encodeURIComponent(transcript)}`);
    };

    recognition.onerror = (event) => {
      setIsListening(false);
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        alert("माइक्रोफ़ोन की अनुमति नहीं दी गई। ब्राउज़र सेटिंग में अनुमति दें।");
      } else if (event.error === "no-speech") {
        alert("कुछ सुनाई नहीं दिया। कृपया पुनः प्रयास करें।");
      }
    };

    recognition.onend = () => setIsListening(false);

    recognition.start();
  };

  const handleSearchSubmit = () => {
    const term = searchQuery.trim();
    if (term) {
      navigate(`/search?q=${encodeURIComponent(term)}`);
    }
  };

  const galleryRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    // Logic to close desktop dropdowns on outside click if needed
    const handler = (e) => {};
    document.addEventListener("mousedown", handler);
    
    // Live Clock Logic
    const updateTime = () => {
      const now = new Date();
      
      // Determine locale based on i18n language
      const locale = i18n.language === 'en' ? 'en-IN' : 'hi-IN';
      
      // Format Date
      const optionsDate = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
      setCurrentDate(now.toLocaleDateString(locale, optionsDate));

      // Format Short Date (for small screens)
      const optionsDateShort = { weekday: 'short', day: 'numeric', month: 'short' };
      setCurrentDateShort(now.toLocaleDateString(locale, optionsDateShort));
      
      // Format Time
      const optionsTime = { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true };
      setCurrentTime(now.toLocaleTimeString(locale, optionsTime).toLowerCase());
    };
    
    updateTime(); // Initial call
    const timerId = setInterval(updateTime, 1000);
    
    return () => {
      document.removeEventListener("mousedown", handler);
      clearInterval(timerId);
    };
  }, [i18n.language]);

  useEffect(() => {
    let isMounted = true;
    const loadBreakingNews = async () => {
      try {
        const res = await getNewsByFilter({ limit: 6 });
        const items = Array.isArray(res?.data) ? res.data : [];
        if (isMounted) {
          setBreakingNews(
            items.map((item) => ({
              id: item.id,
              title: item.news_title || item.title || "",
            }))
          );
        }
      } catch (err) {
        console.error("Error fetching breaking news:", err);
      }
    };
    loadBreakingNews();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Sync Google translate with current i18n language on load
    const syncGoogleTranslate = () => {
      const selectElement = document.querySelector(".goog-te-combo");
      if (selectElement && selectElement.value !== i18n.language) {
        selectElement.value = i18n.language;
        selectElement.dispatchEvent(new Event("change"));
      }
    };

    // Google translate script might take time to load, so check a few times
    const intervalId = setInterval(() => {
      const selectElement = document.querySelector(".goog-te-combo");
      if (selectElement) {
        syncGoogleTranslate();
        clearInterval(intervalId);
      }
    }, 500);

    // clear after 5 seconds just in case
    setTimeout(() => clearInterval(intervalId), 5000);

    return () => clearInterval(intervalId);
  }, [i18n.language]);

  const navBase = "flex items-center gap-1 py-3.5 px-2 text-white font-bold text-[16px] xl:text-[17px] hover:text-brand-red transition-colors whitespace-nowrap tracking-wide";
  
  const toggleDropdown = (menu) => {
    if (openDropdown === menu) {
      setOpenDropdown(null);
    } else {
      setOpenDropdown(menu);
    }
  };

  const handleCategoryChange = (e) => {
    const path = e.target.value;
    if (path) {
      navigate(path);
    }
  };

  const handleLanguageChange = (e) => {
    const newLang = e.target.value;
    i18n.changeLanguage(newLang);
    
    // Trigger Google Translate for dynamic content
    try {
      const selectElement = document.querySelector(".goog-te-combo");
      if (selectElement) {
        selectElement.value = newLang;
        selectElement.dispatchEvent(new Event("change"));
      }
    } catch(err) {
      console.error("Google translate trigger error:", err);
    }
  };

  return (
    <>
      {/* 0. BREAKING NEWS TICKER */}
      <div className="bg-rich-black text-white flex items-center h-9 lg:h-10 text-[13px] lg:text-sm border-b border-dark-purple">
        <div className="bg-brand-red text-white font-bold px-4 lg:px-6 h-full flex items-center justify-center shrink-0 uppercase tracking-widest relative z-10 shadow-lg">
          <span className="animate-pulse mr-2 text-white">●</span> {t('header.breaking_news')}
        </div>
        <div className="overflow-hidden flex-1 relative flex items-center h-full">
          <marquee className="font-semibold tracking-wide" scrollamount="5" onMouseOver={(e) => e.currentTarget.stop()} onMouseOut={(e) => e.currentTarget.start()}>
            {breakingNews.length > 0 ? (
              breakingNews.map((item) => (
                <Link key={item.id} to={`/news/${item.id}`} className="mx-6 hover:underline cursor-pointer">
                  {item.title}
                </Link>
              ))
            ) : (
              <span className="mx-6">{t('header.breaking_news_1')}</span>
            )}
          </marquee>
        </div>
      </div>

      {/* 1. TOP BAR */}
      <div className="bg-rich-black text-gray-300 py-2 border-b border-zinc-800">
        <div className="max-w-[1400px] mx-auto px-4 flex flex-col md:flex-row justify-between items-center text-xs lg:text-sm gap-2 md:gap-0">
          
          {/* Top Left: Call & Social Media Icons */}
          <div className="flex items-center justify-center md:justify-start gap-4 md:gap-5 w-full md:w-auto">
            <div className="flex items-center gap-2 font-bold tracking-wide border-r border-zinc-700 pr-4 md:pr-5 text-white">
              <i className="fa-solid fa-phone text-purple-500 text-base"></i>
              <span className="text-xs md:text-sm">+91 97557 24915</span>
            </div>
            <div className="flex items-center gap-3 md:gap-4">
              <a href="#" className="text-[#1877F2] hover:text-white transition transform hover:scale-110" title="Facebook"><i className="fa-brands fa-facebook-f text-sm md:text-base"></i></a>
              <a href="#" className="text-[#1DA1F2] hover:text-white transition transform hover:scale-110" title="Twitter"><i className="fa-brands fa-twitter text-sm md:text-base"></i></a>
              <a href="#" className="text-[#FF0000] hover:text-white transition transform hover:scale-110" title="YouTube"><i className="fa-brands fa-youtube text-sm md:text-base"></i></a>
              <a href="#" className="text-[#E1306C] hover:text-white transition transform hover:scale-110" title="Instagram"><i className="fa-brands fa-instagram text-sm md:text-base"></i></a>
              <a href="#" className="text-[#25D366] hover:text-white transition transform hover:scale-110" title="WhatsApp"><i className="fa-brands fa-whatsapp text-sm md:text-lg"></i></a>
            </div>
          </div>

          {/* Top Right: Categories & Language Dropdowns */}
          <div className="flex items-center justify-center md:justify-end gap-2 md:gap-6 w-full md:w-auto pt-2 md:pt-0 mt-1 md:mt-0 border-t border-zinc-800 md:border-none flex-wrap">
            <div className="flex items-center gap-1 md:gap-2">
              <span className="text-gray-300 font-bold text-[10px] sm:text-xs">{t('header.categories')}</span>
              <select onChange={handleCategoryChange} className="bg-transparent border border-gray-500 rounded px-1 py-0.5 text-[11px] sm:text-sm text-white outline-none cursor-pointer hover:border-gray-300 font-medium max-w-[100px] sm:max-w-none">
                <option className="bg-rich-black" value="/">{t('sidebar.home')}</option>
                <option className="bg-rich-black" value="/category/national">{t('sidebar.national')}</option>
                <option className="bg-rich-black" value="/category/international">{t('sidebar.international')}</option>
                <option className="bg-rich-black" value="/category/politics">{t('sidebar.politics')}</option>
                <option className="bg-rich-black" value="/category/business">{t('sidebar.business')}</option>
                <option className="bg-rich-black" value="/category/entertainment">{t('sidebar.entertainment')}</option>
                <option className="bg-rich-black" value="/category/sports">{t('sidebar.sports')}</option>
                <option className="bg-rich-black" value="/category/technology">{t('sidebar.tech_science')}</option>
                <option className="bg-rich-black" value="/category/religion">{t('sidebar.religion')}</option>
                <option className="bg-rich-black" value="/category/health">{t('sidebar.health')}</option>
              </select>
            </div>

            <div className="flex items-center gap-1 md:gap-2">
              <span className="text-gray-300 font-bold text-[10px] sm:text-xs">{t('header.language')}</span>
              <select 
                value={i18n.language} 
                onChange={handleLanguageChange} 
                className="bg-transparent border border-gray-500 rounded px-1 py-0.5 text-[11px] sm:text-sm text-white outline-none cursor-pointer hover:border-gray-300 font-medium"
              >
                <option className="bg-rich-black" value="en">English</option>
                <option className="bg-rich-black" value="hi">हिन्दी</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* 2. MAIN HEADER */}
      <header className="bg-white w-full z-50 sticky top-0 lg:relative border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-4 py-3 md:py-4 flex items-center justify-between flex-wrap lg:flex-nowrap">
          
          {/* Mobile top row wrapper */}
          <div className="flex items-center justify-between w-full lg:contents">
            {/* Left: Mobile Hamburger & Desktop Channel Name */}
            <div className="flex items-center justify-start lg:w-[35%] xl:w-[32%] shrink gap-2 min-[380px]:gap-3 md:gap-4">
              <button onClick={() => setMobileMenuOpen(true)} className="lg:hidden shrink-0">
                <i className="fa-solid fa-bars text-2xl md:text-3xl text-black hover:text-brand-red transition"></i>
              </button>
              <div className="flex flex-col items-start justify-center">
                <Link to="/" className="flex flex-col items-start">
                  <span className="text-[14px] min-[360px]:text-[15px] min-[400px]:text-[17px] sm:text-[22px] lg:text-[32px] xl:text-[40px] font-black text-black tracking-wide leading-tight uppercase font-serif drop-shadow-sm hover:text-brand-purple transition-colors whitespace-normal lg:whitespace-nowrap">
                    The Mandakini Times
                  </span>
                </Link>
              </div>
            </div>

            {/* Center: Logo */}
            <div className="flex items-center justify-end lg:justify-center lg:flex-1 shrink-0 ml-2">
              <Link to="/" className="flex shrink-0">
                <img
                  src={logo}
                  alt="मंदाकिनी टाइम्स लोगो"
                  className="h-16 w-16 min-[380px]:h-20 min-[380px]:w-20 md:h-24 md:w-24 lg:h-40 lg:w-40 object-contain bg-white border-[3px] border-white ring-[1px] ring-black shadow-[0_0_20px_rgba(0,0,0,0.4)] rounded-full transform -translate-y-1"
                />
              </Link>
            </div>
          </div>

          {/* Right: Date/Time Widget & Search Bar */}
          <div className="hidden lg:flex flex-col items-end w-1/3 lg:w-[35%] xl:w-[32%] shrink-0 gap-2">
            {/* Live Date, Time & Weather Widget */}
            <div className="flex items-center justify-between w-full bg-brand-purple text-white rounded-md px-2 py-1.5 shadow-sm font-semibold tracking-wide whitespace-nowrap overflow-hidden">
              <div className="flex items-center">
                <i className="fa-regular fa-calendar-days text-[11px] mr-1 opacity-90"></i>
                <span className="text-[11px] xl:text-[13px]">{currentDate}</span>
              </div>
              <span className="mx-1 text-white/50 font-normal">|</span>
              <div className="flex items-center">
                <i className="fa-regular fa-clock text-[11px] mr-1 opacity-90"></i>
                <span className="text-[11px] xl:text-[13px]">{currentTime}</span>
              </div>
              <span className="mx-1 text-white/50 font-normal">|</span>
              <div className="flex items-center gap-1" title={`${weather?.city || "भोपाल"}, ${weather ? weather.temp + "°C" : "--"}, ${weatherLabel}`}>
                <i className={`fa-solid ${weatherIcon} text-brand-red text-[11px]`}></i>
                <span className="text-[11px] xl:text-[13px]">
                  {weather ? `${weather.temp}°C` : "--"} {weatherLabel && <span className="opacity-80">· {weatherLabel}</span>}
                </span>
              </div>
            </div>

            {/* Advanced Search Bar */}
            <div className="relative w-full">
              <div className="flex items-center w-full bg-white border border-gray-300 rounded-md overflow-hidden shadow-sm hover:border-gray-400 focus-within:border-red-500 transition-colors">
                <input 
                  type="text" 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearchSubmit()}
                  placeholder={t('sidebar.search')} 
                  className="w-full px-3 py-1.5 outline-none text-sm text-gray-700 bg-transparent font-medium"
                />
                <button 
                  onClick={handleVoiceSearch}
                  className={`px-3 py-1.5 transition-colors flex items-center justify-center border-l border-gray-200 ${isListening ? 'text-purple-600 bg-purple-50 animate-pulse' : 'text-gray-500 hover:text-brand-red hover:bg-gray-50'}`}
                  title={isListening ? "रोकें" : "वॉइस सर्च"}
                >
                  <i className={`fa-solid ${isListening ? 'fa-microphone-lines' : 'fa-microphone'}`}></i>
                </button>
                <button onClick={handleSearchSubmit} className="bg-purple-700 hover:text-brand-red text-white px-4 py-1.5 transition-colors flex items-center justify-center">
                  <i className="fa-solid fa-search"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Center Date/Time/Weather */}
          <div className="lg:hidden mt-2.5 w-full flex items-center justify-center bg-brand-purple text-white rounded-md px-2 py-1.5 shadow-sm font-semibold tracking-wide text-[10px] min-[400px]:text-[11px] sm:text-xs overflow-hidden">
            <div className="flex items-center min-w-0">
              <i className="fa-regular fa-calendar-days text-xs sm:text-sm mr-1.5 opacity-90 shrink-0"></i>
              <span className="truncate hidden min-[360px]:inline">{currentDate}</span>
              <span className="truncate min-[360px]:hidden">{currentDateShort}</span>
            </div>
            <span className="mx-1 text-white/50 font-normal shrink-0">|</span>
            <div className="flex items-center shrink-0">
              <i className="fa-regular fa-clock text-xs sm:text-sm mr-1.5 opacity-90"></i>
              <span>{currentTime}</span>
            </div>
            <span className="mx-1 text-white/50 font-normal shrink-0">|</span>
            <div className="flex items-center gap-1 shrink-0">
              <i className={`fa-solid ${weatherIcon} text-brand-red text-xs sm:text-sm`}></i>
              <span>{weather ? `${weather.temp}°C` : "--"}{weatherLabel && <span className="opacity-80 hidden sm:inline"> · {weatherLabel}</span>}</span>
            </div>
          </div>

        </div>
      </header>

      {/* 3. NAVIGATION BAR (SRS Structure) */}
      <div className="sticky top-0 left-0 w-full z-50 bg-brand-purple shadow-md border-b-2 border-dark-purple">
        <nav className="max-w-[1400px] mx-auto px-4">
          
          <ul className="max-lg:hidden flex items-center justify-between w-full flex-wrap">
            <li>
              <NavLink to="/" end className="flex items-center py-3 px-3 text-white hover:text-brand-red transition-colors">
                <i className="fa-solid fa-house"></i>
              </NavLink>
            </li>
            
            {/* National */}
            <li className="relative group">
              <NavLink to="/category/national" className={navBase}>
                {t('sidebar.national')} <i className="fa-solid fa-caret-down text-[10px] ml-1"></i>
              </NavLink>
              <div className="absolute left-0 top-full pt-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <div className="bg-brand-purple shadow-xl border-t border-dark-purple min-w-[200px]">
                  <Link to="/category/state" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.state')}</Link>
                  <Link to="/category/district" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.district')}</Link>
                  <Link to="/category/local" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.local_news')}</Link>
                  <Link to="/category/election" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red">{t('sidebar.election')}</Link>
                </div>
              </div>
            </li>

            {/* International */}
            <li><NavLink to="/category/international" className={navBase}>{t('sidebar.international')}</NavLink></li>

            {/* Politics */}
            <li><NavLink to="/category/politics" className={navBase}>{t('sidebar.politics')}</NavLink></li>

            {/* Business */}
            <li className="relative group">
              <NavLink to="/category/business" className={navBase}>
                {t('sidebar.business')} <i className="fa-solid fa-caret-down text-[10px] ml-1"></i>
              </NavLink>
              <div className="absolute left-0 top-full pt-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <div className="bg-brand-purple shadow-xl border-t border-dark-purple min-w-[200px]">
                  <Link to="/category/economy" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.economy')}</Link>
                  <Link to="/category/stock-market" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.stock_market')}</Link>
                  <Link to="/category/finance" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.finance')}</Link>
                  <Link to="/category/real-estate" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red">{t('sidebar.real_estate')}</Link>
                </div>
              </div>
            </li>

            {/* Entertainment */}
            <li className="relative group">
              <NavLink to="/category/entertainment" className={navBase}>
                {t('sidebar.entertainment')} <i className="fa-solid fa-caret-down text-[10px] ml-1"></i>
              </NavLink>
              <div className="absolute left-0 top-full pt-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <div className="bg-brand-purple shadow-xl border-t border-dark-purple min-w-[200px]">
                  <Link to="/category/movies" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.movies')}</Link>
                  <Link to="/category/tv-shows" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.tv_shows')}</Link>
                  <Link to="/category/ott" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.ott')}</Link>
                  <Link to="/category/celebrity" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red">{t('sidebar.celebrity')}</Link>
                </div>
              </div>
            </li>

            {/* Sports */}
            <li className="relative group">
              <NavLink to="/category/sports" className={navBase}>
                {t('sidebar.sports')} <i className="fa-solid fa-caret-down text-[10px] ml-1"></i>
              </NavLink>
              <div className="absolute left-0 top-full pt-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <div className="bg-brand-purple shadow-xl border-t border-dark-purple min-w-[200px]">
                  <Link to="/category/cricket" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.cricket')}</Link>
                  <Link to="/category/football" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.football')}</Link>
                  <Link to="/category/olympics" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red">{t('sidebar.olympics')}</Link>
                </div>
              </div>
            </li>

            {/* Tech/Science */}
            <li><NavLink to="/category/technology" className={navBase}>{t('sidebar.tech_science')}</NavLink></li>

            {/* Religion/Astrology */}
            <li className="relative group">
              <NavLink to="/category/religion" className={navBase}>
                {t('sidebar.religion')} <i className="fa-solid fa-caret-down text-[10px] ml-1"></i>
              </NavLink>
              <div className="absolute left-0 top-full pt-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <div className="bg-brand-purple shadow-xl border-t border-dark-purple min-w-[200px]">
                  <Link to="/category/spirituality" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.spirituality')}</Link>
                  <Link to="/category/astrology" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.astrology')}</Link>
                  <Link to="/category/horoscope" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red">{t('sidebar.horoscope')}</Link>
                </div>
              </div>
            </li>

            {/* Health/Lifestyle */}
            <li className="relative group">
              <NavLink to="/category/health" className={navBase}>
                {t('sidebar.health')} <i className="fa-solid fa-caret-down text-[10px] ml-1"></i>
              </NavLink>
              <div className="absolute left-0 top-full pt-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <div className="bg-brand-purple shadow-xl border-t border-dark-purple min-w-[200px]">
                  <Link to="/category/fitness" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.fitness')}</Link>
                  <Link to="/category/medical" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.medical')}</Link>
                  <Link to="/category/fashion" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.fashion')}</Link>
                  <Link to="/category/travel" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.travel')}</Link>
                  <Link to="/category/food" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red">{t('sidebar.food')}</Link>
                </div>
              </div>
            </li>

            {/* More (अन्य) */}
            <li className="relative group">
              <button className={`${navBase} cursor-default`}>
                {t('sidebar.others')} <i className="fa-solid fa-caret-down text-[10px] ml-1"></i>
              </button>
              <div className="absolute right-0 top-full pt-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <div className="bg-brand-purple shadow-xl border-t border-dark-purple min-w-[200px]">
                  <Link to="/category/crime" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.crime')}</Link>
                  <Link to="/category/education" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.education')}</Link>
                  <Link to="/category/jobs" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.jobs')}</Link>
                  <Link to="/category/agriculture" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.agriculture')}</Link>
                  <Link to="/category/automobile" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.automobile')}</Link>
                  <Link to="/category/weather" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red border-b border-dark-purple/50">{t('sidebar.weather')}</Link>
                  <Link to="/category/opinion" className="block px-4 py-2.5 text-sm text-white hover:text-brand-red">{t('sidebar.opinion')}</Link>
                </div>
              </div>
            </li>

            {/* Burger Menu for Static Pages */}
            <li className="relative group">
              <button className="flex items-center gap-1 py-3.5 px-3 text-white hover:text-brand-red transition-colors cursor-default">
                <i className="fa-solid fa-bars text-xl"></i>
              </button>
              <ul className={`absolute right-0 top-full pt-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50`}>
                <div className="bg-brand-purple shadow-xl border-t border-dark-purple min-w-[220px]">
                  <li><Link to="/about" className="block px-4 py-2.5 text-[15px] font-medium text-white hover:text-brand-red border-b border-dark-purple/50">{t('header.about')}</Link></li>
                  <li><Link to="/team" className="block px-4 py-2.5 text-[15px] font-medium text-white hover:text-brand-red border-b border-dark-purple/50">{t('header.team')}</Link></li>
                  <li><Link to="/blog" className="block px-4 py-2.5 text-[15px] font-medium text-white hover:text-brand-red border-b border-dark-purple/50">{t('header.blog')}</Link></li>
                  <li><Link to="/contact" className="block px-4 py-2.5 text-[15px] font-medium text-white hover:text-brand-red border-b border-dark-purple/50">{t('header.contact')}</Link></li>
                  <li><Link to="/privacy" className="block px-4 py-2.5 text-[15px] font-medium text-white hover:text-brand-red border-b border-dark-purple/50">{t('header.privacy')}</Link></li>
                  <li><Link to="/terms" className="block px-4 py-2.5 text-[15px] font-medium text-white hover:text-brand-red border-b border-dark-purple/50">{t('header.terms')}</Link></li>
                  <li><Link to="/gallery/photos" className="block px-4 py-2.5 text-[15px] font-medium text-white hover:text-brand-red border-b border-dark-purple/50">{t('header.photos')}</Link></li>
                  <li><Link to="/gallery/videos" className="block px-4 py-2.5 text-[15px] font-medium text-white hover:text-brand-red">{t('header.videos')}</Link></li>
                </div>
              </ul>
            </li>
          </ul>
        </nav>
      </div>

      {/* MOBILE MENU (Slide-in) */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[999] bg-dark-purple/60 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}>
          <div className="bg-brand-purple w-[85%] max-w-sm h-full p-6 overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6 border-b border-gray-200 pb-4">
              <img src={logo} alt="logo" className="h-12 w-12 object-contain bg-white border-[3px] border-white ring-[1px] ring-black shadow-[0_0_15px_rgba(0,0,0,0.4)] rounded-full transform -translate-y-0.5" />
              <button onClick={() => setMobileMenuOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-purple-700 hover:text-white transition text-black">
                <i className="fa-solid fa-xmark text-lg"></i>
              </button>
            </div>
            
            <ul className="space-y-1 font-semibold text-white text-[15px]">
              {/* Home */}
              <li><Link to="/" onClick={() => setMobileMenuOpen(false)} className="block py-2.5 hover:text-brand-red border-b border-dark-purple transition"><i className="fa-solid fa-house w-5 text-center mr-2 opacity-80"></i>{t('header.home')}</Link></li>

              {/* National Mobile */}
              <li className="py-2.5 border-b border-gray-100">
                <div className="flex justify-between items-center w-full">
                  <Link to="/category/national" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-red transition flex-1"><i className="fa-solid fa-flag w-5 text-center mr-2 opacity-80"></i>{t('sidebar.national')}</Link>
                  <button onClick={() => toggleDropdown('national')} className="p-2 w-10 h-10 flex items-center justify-center bg-gray-50 text-brand-purple rounded text-sm hover:bg-purple-700 hover:text-white transition">
                    <i className={`fa-solid fa-chevron-${openDropdown === 'national' ? 'up' : 'down'}`}></i>
                  </button>
                </div>
                {openDropdown === 'national' && (
                  <ul className="pl-4 mt-2 space-y-2 text-sm font-medium bg-gray-50 text-gray-800 rounded p-3">
                    <li><Link to="/category/state" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-red block py-1"><i className="fa-solid fa-map-location-dot w-5 text-center mr-2 opacity-80"></i>{t('sidebar.state')}</Link></li>
                    <li><Link to="/category/district" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-red block py-1"><i className="fa-solid fa-city w-5 text-center mr-2 opacity-80"></i>{t('sidebar.district')}</Link></li>
                    <li><Link to="/category/election" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-red block py-1"><i className="fa-solid fa-check-to-slot w-5 text-center mr-2 opacity-80"></i>{t('sidebar.election')}</Link></li>
                  </ul>
                )}
              </li>

              <li><Link to="/category/international" onClick={() => setMobileMenuOpen(false)} className="block py-2.5 hover:text-brand-red border-b border-gray-100 transition"><i className="fa-solid fa-earth-americas w-5 text-center mr-2 opacity-80"></i>{t('sidebar.international')}</Link></li>
              <li><Link to="/category/politics" onClick={() => setMobileMenuOpen(false)} className="block py-2.5 hover:text-brand-red border-b border-gray-100 transition"><i className="fa-solid fa-landmark w-5 text-center mr-2 opacity-80"></i>{t('sidebar.politics')}</Link></li>
              
              {/* Business Mobile */}
              <li className="py-2.5 border-b border-gray-100">
                <div className="flex justify-between items-center w-full">
                  <Link to="/category/business" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-red transition flex-1"><i className="fa-solid fa-briefcase w-5 text-center mr-2 opacity-80"></i>{t('sidebar.business')}</Link>
                  <button onClick={() => toggleDropdown('business')} className="p-2 w-10 h-10 flex items-center justify-center bg-gray-50 text-brand-purple rounded text-sm hover:bg-purple-700 hover:text-white transition">
                    <i className={`fa-solid fa-chevron-${openDropdown === 'business' ? 'up' : 'down'}`}></i>
                  </button>
                </div>
                {openDropdown === 'business' && (
                  <ul className="pl-4 mt-2 space-y-2 text-sm font-medium bg-gray-50 text-gray-800 rounded p-3">
                    <li><Link to="/category/economy" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-red block py-1"><i className="fa-solid fa-coins w-5 text-center mr-2 opacity-80"></i>{t('sidebar.economy')}</Link></li>
                    <li><Link to="/category/stock-market" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-red block py-1"><i className="fa-solid fa-arrow-trend-up w-5 text-center mr-2 opacity-80"></i>{t('sidebar.stock_market')}</Link></li>
                  </ul>
                )}
              </li>

              {/* Entertainment Mobile */}
              <li className="py-2.5 border-b border-gray-100">
                <div className="flex justify-between items-center w-full">
                  <Link to="/category/entertainment" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-red transition flex-1"><i className="fa-solid fa-film w-5 text-center mr-2 opacity-80"></i>{t('sidebar.entertainment')}</Link>
                  <button onClick={() => toggleDropdown('entertainment')} className="p-2 w-10 h-10 flex items-center justify-center bg-gray-50 text-brand-purple rounded text-sm hover:bg-purple-700 hover:text-white transition">
                    <i className={`fa-solid fa-chevron-${openDropdown === 'entertainment' ? 'up' : 'down'}`}></i>
                  </button>
                </div>
                {openDropdown === 'entertainment' && (
                  <ul className="pl-4 mt-2 space-y-2 text-sm font-medium bg-gray-50 text-gray-800 rounded p-3">
                    <li><Link to="/category/movies" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-red block py-1"><i className="fa-solid fa-clapperboard w-5 text-center mr-2 opacity-80"></i>{t('sidebar.movies')}</Link></li>
                    <li><Link to="/category/ott" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-red block py-1"><i className="fa-solid fa-tv w-5 text-center mr-2 opacity-80"></i>{t('sidebar.ott')}</Link></li>
                  </ul>
                )}
              </li>

              {/* Sports Mobile */}
              <li className="py-2.5 border-b border-gray-100">
                <div className="flex justify-between items-center w-full">
                  <Link to="/category/sports" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-red transition flex-1"><i className="fa-solid fa-trophy w-5 text-center mr-2 opacity-80"></i>{t('sidebar.sports')}</Link>
                  <button onClick={() => toggleDropdown('sports')} className="p-2 w-10 h-10 flex items-center justify-center bg-gray-50 text-brand-purple rounded text-sm hover:bg-purple-700 hover:text-white transition">
                    <i className={`fa-solid fa-chevron-${openDropdown === 'sports' ? 'up' : 'down'}`}></i>
                  </button>
                </div>
                {openDropdown === 'sports' && (
                  <ul className="pl-4 mt-2 space-y-2 text-sm font-medium bg-gray-50 text-gray-800 rounded p-3">
                    <li><Link to="/category/cricket" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-red block py-1"><i className="fa-solid fa-baseball-bat-ball w-5 text-center mr-2 opacity-80"></i>{t('sidebar.cricket')}</Link></li>
                  </ul>
                )}
              </li>

              <li><Link to="/category/technology" onClick={() => setMobileMenuOpen(false)} className="block py-2.5 hover:text-brand-red border-b border-gray-100 transition"><i className="fa-solid fa-microchip w-5 text-center mr-2 opacity-80"></i>{t('sidebar.tech_science')}</Link></li>
              <li><Link to="/category/health" onClick={() => setMobileMenuOpen(false)} className="block py-2.5 hover:text-brand-red border-b border-gray-100 transition"><i className="fa-solid fa-heart-pulse w-5 text-center mr-2 opacity-80"></i>{t('sidebar.health')}</Link></li>
              <li><Link to="/category/religion" onClick={() => setMobileMenuOpen(false)} className="block py-2.5 hover:text-brand-red border-b border-gray-100 transition"><i className="fa-solid fa-om w-5 text-center mr-2 opacity-80"></i>{t('sidebar.religion')}</Link></li>

              {/* Gallery Mobile */}
              <li className="py-2.5 border-b border-gray-100">
                <div className="flex justify-between items-center w-full">
                  <span className="hover:text-brand-red transition flex-1 cursor-pointer"><i className="fa-solid fa-images w-5 text-center mr-2 opacity-80"></i>गैलरी</span>
                  <button onClick={() => toggleDropdown('gallery')} className="p-2 w-10 h-10 flex items-center justify-center bg-gray-50 text-brand-purple rounded text-sm hover:bg-purple-700 hover:text-white transition">
                    <i className={`fa-solid fa-chevron-${openDropdown === 'gallery' ? 'up' : 'down'}`}></i>
                  </button>
                </div>
                {openDropdown === 'gallery' && (
                  <ul className="pl-4 mt-2 space-y-2 text-sm font-medium bg-gray-50 text-gray-800 rounded p-3">
                    <li><Link to="/gallery/photos" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-red block py-1"><i className="fa-solid fa-image w-5 text-center mr-2 opacity-80"></i>{t('header.photos')}</Link></li>
                    <li><Link to="/gallery/videos" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-red block py-1"><i className="fa-solid fa-video w-5 text-center mr-2 opacity-80"></i>{t('header.videos')}</Link></li>
                  </ul>
                )}
              </li>

              {/* Important Links / Static Pages Mobile */}
              <li className="py-2.5 border-b border-gray-100">
                <div className="flex justify-between items-center w-full">
                  <span className="hover:text-brand-red transition flex-1 cursor-pointer"><i className="fa-solid fa-link w-5 text-center mr-2 opacity-80"></i>{t('sidebar.others')}</span>
                  <button onClick={() => toggleDropdown('pages')} className="p-2 w-10 h-10 flex items-center justify-center bg-gray-50 text-brand-purple rounded text-sm hover:bg-purple-700 hover:text-white transition">
                    <i className={`fa-solid fa-chevron-${openDropdown === 'pages' ? 'up' : 'down'}`}></i>
                  </button>
                </div>
                {openDropdown === 'pages' && (
                  <ul className="pl-4 mt-2 space-y-2 text-sm font-medium bg-gray-50 text-gray-800 rounded p-3">
                    <li><Link to="/about" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-red block py-1"><i className="fa-solid fa-circle-info w-5 text-center mr-2 opacity-80"></i>{t('header.about')}</Link></li>
                    <li><Link to="/team" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-red block py-1"><i className="fa-solid fa-users w-5 text-center mr-2 opacity-80"></i>{t('header.team')}</Link></li>
                    <li><Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-red block py-1"><i className="fa-solid fa-blog w-5 text-center mr-2 opacity-80"></i>{t('header.blog')}</Link></li>
                    <li><Link to="/privacy" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-red block py-1"><i className="fa-solid fa-shield-halved w-5 text-center mr-2 opacity-80"></i>{t('header.privacy')}</Link></li>
                    <li><Link to="/terms" onClick={() => setMobileMenuOpen(false)} className="hover:text-brand-red block py-1"><i className="fa-solid fa-file-contract w-5 text-center mr-2 opacity-80"></i>{t('header.terms')}</Link></li>
                  </ul>
                )}
              </li>

              <li><Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="block py-2.5 hover:text-brand-red transition"><i className="fa-solid fa-envelope w-5 text-center mr-2 opacity-80"></i>{t('header.contact')}</Link></li>
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
